/** @jsxImportSource @emotion/react */

import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { useHideBodyOverflow, useKeydown } from '@btime/react-hooks'
import React from 'react'

import { SmallText, Title } from '../presentation/Text'
import Icon from '../shapes/Icon'
import Link from '../actions/Link'
import Panel, { PanelContent } from './Panel'
import breakpoints from '../../utils/constants/breakpoints'

const ModalPanel = ({
  children,
  onClose,
  fullScreen,
  width,
  disableClickOutside,
  rawContent,
  level,
  edgeToEdge,
  noCloseButton,
  ...props
}) => {
  const wrapperRef = React.useRef()
  // useClickOutside(wrapperRef, { action: onClose, condition: !!onClose && !disableClickOutside && !tourOpen })
  useKeydown(wrapperRef, { keys: ['Escape'], action: onClose || (() => {}) })
  useHideBodyOverflow()
  const levelPadding = level > 1 ? (level - 1) * 2.5 : 0

  if (!width && fullScreen) width = '97%'
  if (!width && edgeToEdge) width = '100%'
  width = width || '32rem'

  let paddingTop = `padding-top: ${levelPadding + 1.5}rem;`
  let paddingBottom = 'padding-bottom: 1.5rem;'
  if (fullScreen) {
    paddingTop = 'padding-top: 3vh;'
    paddingBottom = ''
  }

  if (edgeToEdge) {
    paddingTop = ''
    paddingBottom = ''
  }

  const style = css`
    width: ${width};
    max-width: 100%;
    max-height: 100%;
    transition: all 0.5s ease-in-out;
    transform-origin: left top;

    & > .contentWrap {
      position: relative;
      ${paddingTop}
      ${paddingBottom}
    }
  `

  return (
    <div css={style} ref={wrapperRef}>
      <div className="contentWrap">
        <ModalCloseButton
          onClose={onClose}
          hide={edgeToEdge || fullScreen || noCloseButton}
          levelPadding={levelPadding}
        />
        <Panel //
          noRadius={edgeToEdge}
          ref={wrapperRef}
          noPadding
          noMargin={fullScreen || edgeToEdge}
          minHeight={edgeToEdge ? '100vh' : fullScreen ? '97vh' : ''}
          {...props}
        >
          {rawContent}
          <PanelContent {...props}>{children}</PanelContent>
        </Panel>
      </div>
    </div>
  )
}

const ModalCloseButton = ({ onClose, hide, levelPadding }) => {
  if (!onClose || !!hide) return false

  const style = css`
    background: #ffffff80;
    position: absolute;
    top: ${levelPadding + 0.5}rem;
    right: -1rem;
    border-radius: 5px;
    height: 1.5rem;
    width: 1.5rem;
    & > a {
      padding: 0.15rem;
    }

    @media (max-width: ${breakpoints.md}px) {
      background: #00000040;
      right: 1.5rem;
      top: 3.4rem;
    }
  `

  return (
    <div css={style}>
      <Link flex onClick={onClose}>
        <Icon name="BsX" color={COLORS.white} />
      </Link>
    </div>
  )
}

export const ModalHeader = ({ title, subtitle, children }) => {
  const style = css`
    border-radius: 5px 5px 0 0;
    background: ${COLORS.purple};
    padding: 2rem 1.5rem;
  `

  return (
    <div css={style}>
      <Title size="h4" color={COLORS.white} noMargin>
        {title}
      </Title>
      <SmallText color={COLORS.white}>{subtitle}</SmallText>
      {children}
    </div>
  )
}

const ModalWrapper = ({ noOverlay, zIndex, children }) => {
  const style = css`
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: ${zIndex || 1000};
    background-color: ${noOverlay ? COLORS.transparent : '#00000070'};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: scroll;
  `

  return <div css={style}>{children}</div>
}

const Modal = ({ open, onClose, children, noOverlay, zIndex, ...props }) => {
  if (!open) return false

  return (
    <ModalWrapper zIndex={zIndex} noOverlay={noOverlay}>
      <ModalPanel onClose={onClose} {...props}>
        {children}
      </ModalPanel>
    </ModalWrapper>
  )
}

export default Modal
